import { useTranslation } from "react-i18next";
import { useCurrentDataContext } from "../../../../contexts/CurrentDataContext";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import classes from "./FormAccomplishmentsUseAttachment.module.css";
import { FiCalendar } from "react-icons/fi";
import { BsX } from "react-icons/bs";
import FileDropZone from "../../../Assets/FileDropZone/FileDropZone";
import { Accomplishment } from "../../../../contexts/interfaces";

// Datepicker imports
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useRequiredAccomplishmentsContext } from "../../../../contexts/RequiredAccomplishmentsContext";
import { requiredVaccination } from "../../../../utils/requiredVaccination";
import { FormData } from "../../../../contexts/interfaces";
// Import dateLocales utility to ensure locale is registered correctly
import "../../../../utils/dateLocales";
import {
  getMinSelectableAccomplishmentDate,
  isAccomplishmentDateWithinMaxAge,
} from "../../../../utils/accomplishmentDateRules";

const FormAccomplishmentsUseAttachment = () => {
  const { t } = useTranslation();

  const { currentData, setCurrentData } = useCurrentDataContext();

  const { language } = useLanguageContext();

  const { requiredAccomplishments } = useRequiredAccomplishmentsContext();

  const checkDate = (accomplishmentType: string) => {
    let date = undefined;

    date =
      currentData?.accomplishments && currentData?.accomplishments?.length > 0
        ? currentData?.accomplishments?.find(
            (accomplishment) => accomplishment.type === accomplishmentType
          )?.date
        : undefined;

    date = typeof date === "string" ? new Date(date) : undefined;

    return date;
  };

  const minSelectableDate = getMinSelectableAccomplishmentDate();

  const changeDate = (date: Date | null, type: string) => {
    if (date && !isAccomplishmentDateWithinMaxAge(date)) {
      return;
    }
    if (date && type) {
      // Timezone modification, otherwise day is 1 day off
      date = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

      // Check if accomplishment already exists
      const index =
        currentData?.accomplishments &&
        currentData?.accomplishments.findIndex(
          (accomplishment) => accomplishment.type === type
        );
      // If it doesnt exists, create new
      if (index === -1) {
        const newAcc: Accomplishment = {
          id: +Date.now().toString().slice(-8) + Math.floor(Math.random() * 10),
          title:
            requiredAccomplishments?.find((acc) => acc.type === type)?.title ||
            "",
          date: date.toISOString(),
          fromAttachment: true,
          type: type,
          approved: false,
          sourceType: `attachment_${type}`,
        };

        const newCurrentData: FormData = {
          ...currentData,
          accomplishments: currentData?.accomplishments
            ? [...currentData?.accomplishments, newAcc]
            : [{ ...newAcc }],
        };

        setCurrentData({ ...newCurrentData });
      } else if (
        currentData?.accomplishments &&
        index !== -1 &&
        index !== undefined &&
        currentData?.accomplishments[index].date
      ) {
        // If accomplishment already exists, just modify date
        let newAccomplishments = currentData.accomplishments;
        // Datefix, or date might be 1 day off
        const newDate = new Date(
          date.getTime() - date.getTimezoneOffset() * 60000
        );
        // Update date to temporary newAccomplishment
        newAccomplishments[index].date = newDate.toISOString();
        // Update currentData with newAccomplishments
        setCurrentData({
          ...currentData,
          accomplishments: newAccomplishments,
        });
      }
    }
  };

  const removeDate = (type: string) => {
    const index =
      currentData?.accomplishments &&
      currentData?.accomplishments.findIndex(
        (accomplishment) => accomplishment.type === type
      );

    let modifiedAccomplishments = currentData?.accomplishments;

    if (modifiedAccomplishments && index !== undefined) {
      modifiedAccomplishments.splice(index, 1);
    }

    currentData?.accomplishments &&
      modifiedAccomplishments &&
      setCurrentData({
        ...currentData,
        accomplishments: modifiedAccomplishments,
      });
  };

  return (
    <>
      <div className={classes.checkRow}>
        <input
          className={classes.useAttachmentsCheckbox}
          type="checkbox"
          checked={currentData?.useAttachments || false}
          onChange={() =>
            currentData &&
            setCurrentData({
              ...currentData,
              useAttachments: !currentData?.useAttachments,
              accomplishments: [],
              records: currentData.records
                ? [
                    ...currentData.records?.filter(
                      (record) => record.type !== "accomplishment"
                    ),
                  ]
                : [],
            })
          }
        />
        <div className={classes.formChoosePermitsCheckboxText}>
          {t("formChosenEmployee.useAttachments")}
        </div>
      </div>

      {currentData?.useAttachments === true ? (
        <>
          <table className={classes.table}>
            <tbody>
              <tr>
                <th>{t("formChosenEmployee.toFillAccomplishment")}</th>
                <th>{t("formChosenEmployee.date")}</th>
              </tr>

              <tr>
                <td>{t("formChosenEmployee.theory")}</td>
                <td className={classes.bolded}>
                  <div className={classes.formChoosePermitsRow}>
                    <DatePicker
                      locale={language}
                      selected={
                        checkDate("medtheory") // Theory
                      }
                      peekNextMonth
                      showYearDropdown
                      showMonthDropdown
                      dateFormat="dd.MM.yyyy"
                      dropdownMode="select"
                      onChange={(date) => {
                        changeDate(date, "medtheory"); // Theory
                      }}
                      minDate={minSelectableDate}
                      maxDate={new Date()}
                    />
                    <FiCalendar className={classes.calendarIcon} />

                    <div
                      onClick={() => removeDate("medtheory")} // Theory
                    >
                      <BsX className={classes.bsxIcon} />
                    </div>
                  </div>
                </td>
              </tr>

              <tr>
                <td>{t("formChosenEmployee.math")}</td>
                <td className={classes.bolded}>
                  <div className={classes.formChoosePermitsRow}>
                    <DatePicker
                      locale={language}
                      selected={checkDate("medcalculations")} // Math
                      peekNextMonth
                      showYearDropdown
                      showMonthDropdown
                      dateFormat="dd.MM.yyyy"
                      dropdownMode="select"
                      onChange={(date) => {
                        changeDate(date, "medcalculations"); // Math
                      }}
                      minDate={minSelectableDate}
                      maxDate={new Date()}
                    />
                    <FiCalendar className={classes.calendarIcon} />

                    <div
                      onClick={() => removeDate("medcalculations")} // Math
                    >
                      <BsX className={classes.bsxIcon} />
                    </div>
                  </div>
                </td>
              </tr>

              <tr>
                <td>{t("formChosenEmployee.pkv")}</td>
                <td className={classes.bolded}>
                  <div className={classes.formChoosePermitsRow}>
                    <DatePicker
                      locale={language}
                      selected={checkDate("cns")} // CNS (PKV)
                      peekNextMonth
                      showYearDropdown
                      showMonthDropdown
                      dateFormat="dd.MM.yyyy"
                      dropdownMode="select"
                      onChange={(date) => {
                        changeDate(date, "cns"); // CNS (PKV)
                      }}
                      minDate={minSelectableDate}
                      maxDate={new Date()}
                    />
                    <FiCalendar className={classes.calendarIcon} />

                    <div
                      onClick={() => removeDate("cns")} // CNS (PKV)
                    >
                      <BsX className={classes.bsxIcon} />
                    </div>
                  </div>
                </td>
              </tr>

              {
                // Vaccination is conditional, depending on permit choises. Show only, if vaccination permit is checked
                requiredAccomplishments?.findIndex(
                  (a) => a.type === requiredVaccination.type
                ) !== -1 && (
                  <tr>
                    <td>{t("formChosenEmployee.vaccination")}</td>
                    <td className={classes.bolded}>
                      <div className={classes.formChoosePermitsRow}>
                        <DatePicker
                          locale={language}
                          selected={checkDate(requiredVaccination.type)} // Vaccination
                          peekNextMonth
                          showYearDropdown
                          showMonthDropdown
                          dateFormat="dd.MM.yyyy"
                          dropdownMode="select"
                          onChange={(date) => {
                            changeDate(date, requiredVaccination.type); // Vaccination
                          }}
                          maxDate={new Date()}
                        />
                        <FiCalendar className={classes.calendarIcon} />

                        <div
                          onClick={() => removeDate(requiredVaccination.type)} // Vaccination
                        >
                          <BsX className={classes.bsxIcon} />
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              }
            </tbody>
          </table>

          <div className={classes.fileDropZone}>
            <div className={classes.askAttachmentText}>
              {t("formChosenEmployee.accomplishmentAttachmentsTitle")}
            </div>
            <FileDropZone fileType="accomplishment" />
          </div>
        </>
      ) : null}
    </>
  );
};

export default FormAccomplishmentsUseAttachment;
